import EditPageContainer from '@app/components/EditPageContainer'
import {
  ITransactionHistory,
  TransactionHistory,
} from '@app/models/TransactionHistory'
import {
  IGlobalConfigurationState,
  IGlobalPropertiesState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link, RouteComponentProps } from 'react-router-dom'
import { Alert, Button, Col, Container, Input, Row } from 'reactstrap'
import {
  actionCreators,
  ISuccessStoriesEditState,
} from '../modules/successStoriesEdit'
import AssociatedPropertySelector from '@app/components/AssociatedPropertySelector'
import { SuccessStory } from '@app/models/SuccessStory'
import { formatAddressSingleLine } from '@app/utils/formatting'
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import { ImageSize } from '@app/models/enums'
import { IAttachment } from '@app/models/Attachment'
import { List } from 'immutable'
import Image from '@app/components/Image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface IConnectionViewRouterProps {
  id: string
}

interface IComponentState {
  showConfirmDelete: boolean
}

type IComponentProps = ISuccessStoriesEditState &
  IGlobalPropertiesState &
  IGlobalConfigurationState &
  RouteComponentProps<IConnectionViewRouterProps> &
  typeof actionCreators

export default class SuccessStoryEditPage extends React.PureComponent<
  IComponentProps,
  IComponentState
> {
  constructor(props: IComponentProps) {
    super(props)

    bindFunctions(this)

    this.state = {
      showConfirmDelete: false,
    }
  }

  public componentDidMount() {
    if (this.props.match.params.id) {
      if (this.props.match.path.includes('edit')) {
        this.props.setCanEdit(true)
      }
      this.props.loadSuccessStory(this.props.match.params.id)
    } else {
      this.props.setCanEdit(true)
    }
  }

  public componentWillUnmount() {
    this.props.resetState()
  }

  public render() {
    if (this.props.isLoading || this.props.isSaving) {
      return (
        <DocumentTitle
          title={
            this.props.match.params.id
              ? this.props.match.path.includes('edit')
                ? 'Edit Success Story'
                : this.props.successStory.title
              : 'New Success Story'
          }
        >
          <Container className='my-3'>
            <div className='mb-3 text-center'>
              Retrieving Success Story Information
            </div>
            {this.props.error && (
              <Alert color='danger'>{this.props.error}</Alert>
            )}
          </Container>
        </DocumentTitle>
      )
    }

    return (
      <EditPageContainer
        saveDisabled={
          this.props.isSaving ||
          !this.props.successStory.property ||
          !this.props.successStory.title
        }
        title={
          this.props.match.params.id
            ? this.props.match.path.includes('edit')
              ? 'Edit Success Story'
              : this.props.successStory.title
            : 'New Success Story'
        }
        hideButtons={!this.props.canEdit}
        onSaveClick={this._onSave}
        onCancelClick={this._onCancel}
      >
        {this._renderSuccessStory()}
      </EditPageContainer>
    )
  }
  private _renderSuccessStory() {
    return (
      <div className='container pt-2 pb-5'>
        {this.props.canEdit ? (
          <Input
            type='text'
            height={'155px'}
            placeholder='Enter Title'
            invalid={!this.props.successStory.title}
            value={this.props.successStory.title}
            onChange={(e) => this.props.setTitle(e.target.value)}
          />
        ) : (
          <Row>
            <Col>
              <h5>{this.props.successStory.title}</h5>
            </Col>
            {!this.props.canEdit && (
              <>
                <Button
                  color='primary'
                  className='mr-3'
                  onClick={() => this.props.setCanEdit(true)}
                >
                  Edit
                </Button>
                <Button
                  color='secondary'
                  className='mr-3'
                  onClick={this._toggleDelete}
                >
                  Delete
                  <DeleteConfirmationModal
                    isOpen={this.state.showConfirmDelete}
                    itemName={this.props.successStory.title}
                    onConfirm={() =>
                      this.props.deleteSuccessStory(this.props.successStory.id)
                    }
                    onToggleModal={this._toggleDelete}
                  />
                </Button>
              </>
            )}
          </Row>
        )}
        <Row className='mt-3'>
          <Col>
            {this.props.canEdit ? (
              <AssociatedPropertySelector
                onChange={this.props.setProperty}
                placeholder='Select property'
                invalid={this.props.successStory.property ? false : true}
                value={
                  this.props.successStory
                    ? this.props.successStory.property
                    : undefined
                }
              />
            ) : (
              <>
                <Row>
                  <Col>
                    <text style={{ fontWeight: 'bold' }}>Property: </text>
                    <Link
                      to={`/properties/property/${
                        this.props.successStory.property
                          ? this.props.successStory.property.propertyId
                          : ''
                      }`}
                    >
                      {this.props.successStory.property
                        ? this.props.successStory.property.name
                        : 'Loading'}
                    </Link>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <text style={{ fontWeight: 'bold' }}>Address: </text>
                    {this.props.successStory.property
                      ? formatAddressSingleLine(
                          this.props.successStory.property.address,
                        )
                      : 'Loading'}
                  </Col>
                </Row>
              </>
            )}
          </Col>
        </Row>
        <h5 className='section-title'>Description</h5>
        <Input
          type='textarea'
          placeholder={this.props.canEdit ? 'Enter a description...' : ''}
          value={this.props.successStory.description}
          style={{ height: '150px' }}
          disabled={!this.props.canEdit}
          onChange={(e) => this.props.setDescription(e.target.value)}
        />
        <h5 className='section-title'>Outline</h5>
        <Input
          type='textarea'
          placeholder={this.props.canEdit ? 'Enter outline...' : ''}
          value={this.props.successStory.outline}
          disabled={!this.props.canEdit}
          style={{ height: '150px' }}
          onChange={(e) => this.props.setOutline(e.target.value)}
        />
        <h5 className='section-title'>Challenge</h5>
        <Input
          type='textarea'
          placeholder={this.props.canEdit ? 'Enter challenge...' : ''}
          value={this.props.successStory.challenge}
          disabled={!this.props.canEdit}
          style={{ height: '150px' }}
          onChange={(e) => this.props.setChallenge(e.target.value)}
        />
        <h5 className='section-title'>Action</h5>
        <Input
          type='textarea'
          placeholder={this.props.canEdit ? 'Enter action...' : ''}
          value={this.props.successStory.action}
          disabled={!this.props.canEdit}
          style={{ height: '150px' }}
          onChange={(e) => this.props.setAction(e.target.value)}
        />
        <h5 className='section-title'>Results</h5>
        <Input
          type='textarea'
          placeholder={this.props.canEdit ? 'Enter results...' : ''}
          value={this.props.successStory.results}
          disabled={!this.props.canEdit}
          style={{ height: '150px' }}
          onChange={(e) => this.props.setResults(e.target.value)}
        />
        <h5 className='section-title'>Property Photos</h5>
        {this._renderImages(this.props.propertyPhotos)}
      </div>
    )
  }

  private _renderImages(photos: List<IAttachment>) {
    if (this.props.loadingPhotos) {
      return <div>Loading Photos</div>
    } else if (
      !this.props.loadingPhotos &&
      this.props.propertyPhotos.size === 0
    ) {
      return <div>Property has no photos</div>
    }
    return (
      <Row style={{ marginLeft: 10 }}>
        {photos.toArray().map((photo) => {
          const selectedImage =
            this.props.successStory.propertyPhoto &&
            this.props.successStory.propertyPhoto.id === photo.id
          return (
            <div
              key={photo.id}
              className='pb-3 text-primary align-items-start'
              id={photo.id}
              style={{ flex: 1, flexGrow: 0, marginRight: 10 }}
            >
              <div style={{}}>
                {selectedImage ? (
                  <FontAwesomeIcon
                    icon='check-circle'
                    size='lg'
                    style={{ zIndex: 4, position: 'absolute', margin: 10 }}
                  />
                ) : (
                  <></>
                )}
                <Image
                  key={photo.id}
                  defaultImageUrl='/images/default-image_h_100_w_100.png'
                  height={200}
                  image={photo}
                  style={
                    selectedImage
                      ? {
                          borderColor: 'primary',
                          border: 1,
                          borderStyle: 'solid',
                          borderWidth: 5,
                        }
                      : {}
                  }
                  onClick={() => {
                    if (this.props.canEdit) {
                      this.props.setPropertyPhoto(photo)
                    }
                  }}
                  size={ImageSize.h500w500}
                  width={175}
                />
              </div>
            </div>
          )
        })}
      </Row>
    )
  }

  private _onCancel() {
    this.props.setCanEdit(false)
    if (this.props.match.params.id) {
      this.props.loadSuccessStory(this.props.match.params.id)
    } else {
      this.props.history.goBack()
    }
  }

  private _onSave() {
    this.props.save(this.props.successStory as SuccessStory)
    this.props.resetState()
  }

  private _toggleDelete() {
    this.setState({ showConfirmDelete: !this.state.showConfirmDelete })
  }
}
